<template>
	<CDeprecatedValidatedInput
		id="password"
		ref="passwordRef"
		class="create-password-input mb-2"
		:value="value"
		type="password"
		:placeholder="placeholderText"
		name="Password"
		rules="password"
		:minlength="7"
		validation-mode="aggressive"
		autocomplete="new-password"
		:autofocus="autoFocus"
		@input="handlePasswordInput"
		@change="$emit('change', $event)"
	>
		<template #error><span></span></template>
		<template #help-text="{ errors, invalid }">
			<div v-if="passwordScore > 0" class="create-password-input__strength">
				<ElProgress :percentage="passwordPercent" :status="passwordStatus" />
				<span class="create-password-input__help">{{ passwordStrength }}</span>
			</div>
			<div v-if="invalid" class="password-help input--error">
				{{ errors[0] || 'Use at least 7 characters.' }}
			</div>
		</template>
	</CDeprecatedValidatedInput>
</template>

<script lang="ts">
	import CDeprecatedValidatedInput from '@cognitoforms/shared-components/src/components/form-controls/DeprecatedValidatedInput.vue';
	import { Progress } from '@cognitoforms/element-ui';
	import { extend } from 'vee-validate';
	import { defineComponent, computed, ref } from 'vue';
	const scorePassword = function (text: string) {
		return import(/* webpackPrefetch: true */ 'zxcvbn')
			.then(m => m.default)
			.then(zxcvbn => zxcvbn(text));
	};

	extend('password', {
		computesRequired: true,
		validate: async text => {
			if (text.length < 7) {
				return 'Use at least 7 characters.';
			}
			const {
				score,
				feedback: { warning }
			} = await scorePassword(text);
			if (score > 1)
				return true;
			if (!warning)
				return 'Password is too weak.';
			if (/repeat/gi.test(warning))
				return 'Avoid repeated words and characters.';
			if (/(common|sequence)/gi.test(warning))
				return 'This password is too common.';
			return true;
		}
	});
	export default defineComponent({
		name: 'CreatePasswordInput',
		components: {
			CDeprecatedValidatedInput,
			ElProgress: Progress
		},
		props: {
			value: { type: String, default: '' },
			placeholderText: { type: String, default: 'Password' },
			autoFocus: { type: Boolean, default: false }
		},
		emits: ['change', 'input'],
		setup(props, { emit }) {
			const passwordScore = ref<number>(0);
			const passwordStatus = computed(() => {
				return passwordScore.value < 2 ? 'exception' : passwordScore.value < 4 ? 'warning' : 'success';
			});
			const passwordPercent = computed(() => {
				return passwordScore.value * 25;
			});
			const passwordStrength = computed(() => {
				return passwordScore.value < 2 ? 'Weak' : passwordScore.value < 4 ? 'Medium' : 'Strong';
			});
			const handlePasswordInput = async (text) => {
				emit('input', text);
				passwordScore.value = text.length < 7 ? 0 : (await scorePassword(text)).score;
			};
			return {
				passwordScore,
				passwordStatus,
				passwordPercent,
				passwordStrength,
				handlePasswordInput
			};
		}
	});
</script>
<style lang="scss">
	.create-password-input {
		min-height: 80px;

		&__strength {
			display: flex;
			align-items: baseline;
			flex-direction: row;

			.el-progress {
				flex-grow: 1;
			}

			span {
				margin-left: .5em;
			}
		}

		&__help {
			font-size: 12px;
		}
	}

	.cog-input + .password-help {
		margin-top: .5em;
	}

	.el-progress {
		position: relative;
		line-height: 1;

		&--without-text {

			.el-progress__text {
				display: none;
			}

			.el-progress-bar {
				display: block;
				margin-right: 0;
				padding-right: 0;
			}
		}

		&.is-success {

			.el-progress-bar__inner {
				background-color: var(--green-5);
			}

			.el-progress__text {
				color: var(--green-5);
			}
		}

		&.is-warning {

			.el-progress-bar__inner {
				background-color: var(--alert-6);
			}

			.el-progress__text {
				color: var(--alert-6);
			}
		}

		&.is-exception {

			.el-progress-bar__inner {
				background-color: var(--border-error);
			}

			.el-progress__text {
				color: var(--text-error);
			}
		}
	}

	.el-progress-bar {
		display: inline-block;
		width: 100%;
		box-sizing: border-box;
		margin-right: -55px;
		vertical-align: middle;

		&__outer {
			position: relative;
			height: 6px;
			background-color: var(--bg-progress-incomplete);
			border-radius: 100px;
			overflow: hidden;
			vertical-align: middle;
		}

		&__inner {
			position: absolute;
			left: 0;
			top: 0;
			height: 100%;
			background-color: var(--bg-progress-complete);
			border-radius: 100px;
			line-height: 1;
			text-align: right;
			white-space: nowrap;
			transition: width .6s ease;
		}
	}

	@keyframes progress {

		0% {
			background-position: 0 0;
		}

		100% {
			background-position: 32px 0;
		}
	}
</style>